/**
 * Admin data layer: fetch programs, programTemplates, equipment, exercises,
 * warmup_templates, finisher_templates from Firestore.
 * Firestore + the CMS are the single source of truth; content is authored here.
 * `npm run backup:content` exports these collections back to the src/data JSON
 * files as a version-controlled backup (no JSON to Firestore sync runs anymore).
 */
import { db } from '../firebase';
import { PUBLISHED_PROGRAM_IDS } from '../data/programs';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';

/**
 * Remove undefined, NaN, Infinity from payloads. Firestore rejects these.
 * Uses JSON round-trip to strip unsupported values reliably.
 */
function stripUndefined(obj) {
  if (obj === undefined || obj === null) return obj;
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
}

function getProgramListId(p) {
  if (p.id) return p.id;
  if (p.programId) return p.programId;
  return null;
}

/**
 * @returns {Promise<Array<{ id: string, name: string, type: 'catalog' | 'template' }>>}
 */
export async function getProgramList() {
  const [programsSnap, templatesSnap] = await Promise.all([
    getDocs(collection(db, 'programs')),
    getDocs(collection(db, 'programTemplates')),
  ]);

  const catalogIds = new Set();
  const catalog = programsSnap.docs.map((d) => {
    const p = { id: d.id, ...d.data() };
    const id = getProgramListId(p);
    if (id) catalogIds.add(id);
    const published = p.published === true || (p.published !== false && PUBLISHED_PROGRAM_IDS.includes(id));
    return { id, name: p.displayName || p.name || p.id, displayName: p.displayName || p.name, type: 'catalog', published };
  });

  const templates = templatesSnap.docs
    .map((d) => {
      const p = { id: d.id, ...d.data() };
      const id = getProgramListId(p);
      const published = p.published === true || (p.published !== false && PUBLISHED_PROGRAM_IDS.includes(id));
      return { id, catalogProgramId: p.catalogProgramId, name: p.displayName || p.name || p.programId || p.id, displayName: p.displayName || p.name, type: 'template', published };
    })
    .filter((t) => t.id && !catalogIds.has(t.id) && !catalogIds.has(t.catalogProgramId));

  return [...catalog, ...templates].filter((r) => r.id);
}

/**
 * @param {string} listId — id from program list (catalog id or template id)
 * @returns {Promise<object | null>}
 */
export async function getProgramByListId(listId) {
  if (!listId) return null;

  const [programDoc, templateDoc] = await Promise.all([
    getDoc(doc(db, 'programs', listId)),
    getDoc(doc(db, 'programTemplates', listId)),
  ]);

  let merged = null;

  if (programDoc.exists()) {
    merged = { id: programDoc.id, ...programDoc.data() };
  }

  if (templateDoc.exists()) {
    const tpl = { id: templateDoc.id, ...templateDoc.data() };
    merged = merged ? { ...tpl, ...merged } : tpl;
  }

  // If catalog exists but template is under a different ID (e.g. catalogProgramId link),
  // find the linked template and merge its macrocycle/mesocycle data.
  if (merged && !templateDoc.exists()) {
    try {
      const q = query(collection(db, 'programTemplates'), where('catalogProgramId', '==', listId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const linkedTpl = snap.docs[0].data();
        if (!merged.mesocycles && Array.isArray(linkedTpl.macrocycles)) {
          merged = { ...linkedTpl, ...merged, _templateSource: snap.docs[0].id };
        }
      }
    } catch (_) { /* query may fail on missing index — non-fatal */ }
  }

  if (!merged) return null;

  // Extract nested mesocycles from macrocycles when no top-level mesocycles array exists
  if ((!merged.mesocycles || merged.mesocycles.length === 0) && Array.isArray(merged.macrocycles)) {
    const extracted = merged.macrocycles.flatMap((macro) => {
      const macroId = macro.id || macro.macrocycle_id || macro.macrocycleId;
      return (macro.mesocycles || []).map((m) => ({
        id: m.id || m.mesocycle_id || m.mesocycleId,
        mesocycle_id: m.id || m.mesocycle_id || m.mesocycleId,
        macrocycle_id: macroId,
        name: m.name,
        order: m.order,
        weeks: m.weeks || (m.startWeek && m.endWeek ? `${m.startWeek}-${m.endWeek}` : ''),
        duration_weeks: m.totalWeeks || m.duration_weeks || 4,
        focus: m.focus || m.phaseDescription || '',
        strengthPhase: m.strengthPhase,
        repRanges: m.repRanges,
        intensity: m.intensity,
        workout_templates: m.workout_templates || [],
        weekly_schedule: m.weekly_schedule || {},
        progression_rules: m.progression_rules || {},
      }));
    });
    if (extracted.length > 0) {
      merged.mesocycles = extracted;
    }
  }

  return merged;
}

/**
 * Save program catalog or runtime template to Firestore.
 * Writes to programTemplates/{id} if that doc exists, otherwise programs/{id}.
 * @param {string} id — program or template doc id (e.g. multi_sport_sprint or sam-multi-sport-sprint-triathlon-2026)
 * @param {object} data — fields to save (merge). Catalog: name, description, goals, equipment_required, equipment_optional, exercise_ids, warmup_template_ids, finisher_template_ids, duration_months, etc. Template: name, programId, weeklyTemplates, keyEvents, etc.
 */
export async function saveProgram(id, data) {
  if (!id) return;
  const templateRef = doc(db, 'programTemplates', id);
  const programRef = doc(db, 'programs', id);
  const templateSnap = await getDoc(templateRef);
  const ref = templateSnap.exists() ? templateRef : programRef;
  const payload = stripUndefined({ ...data, updatedAt: new Date().toISOString() });
  await setDoc(ref, payload, { merge: true });
}

/**
 * Delete a program from Firestore. Removes from both programs/ and programTemplates/ if present.
 * Only deletes drafts (published !== true) unless force is set.
 */
export async function deleteProgram(id, { force = false } = {}) {
  if (!id) return;
  const programRef = doc(db, 'programs', id);
  const templateRef = doc(db, 'programTemplates', id);

  if (!force) {
    const snap = await getDoc(programRef);
    if (snap.exists() && snap.data().published === true) {
      throw new Error('Cannot delete a published program. Unpublish it first.');
    }
  }

  const programSnap = await getDoc(programRef);
  const templateSnap = await getDoc(templateRef);
  if (programSnap.exists()) await deleteDoc(programRef);
  if (templateSnap.exists()) await deleteDoc(templateRef);
}

/**
 * Generate a new template version string for publishing.
 * Format: YYYY-MM-DD-v{shortId} e.g. 2026-02-16-vm5x2k
 */
function generateTemplateVersion() {
  const date = new Date().toISOString().slice(0, 10);
  const shortId = Date.now().toString(36).slice(-6);
  return `${date}-v${shortId}`;
}

/**
 * Publish program: save all data and bump templateVersion so users see "Program update available".
 * Updates both the doc being edited and programs/{catalogProgramId} for checkProgramUpdateAvailable.
 * @param {string} id — program or template doc id
 * @param {object} data — full program data to save
 * @param {string} [catalogProgramId] — program id for user lookup (e.g. multi_sport_sprint). If omitted, uses id.
 * @returns {Promise<{ templateVersion: string }>}
 */
export async function publishProgram(id, data, catalogProgramId) {
  if (!id) throw new Error('Program id required');
  const templateVersion = generateTemplateVersion();
  const catalogId = catalogProgramId || id;
  const now = new Date().toISOString();

  const templateRef = doc(db, 'programTemplates', id);
  const programRef = doc(db, 'programs', id);
  const templateSnap = await getDoc(templateRef);
  const ref = templateSnap.exists() ? templateRef : programRef;

  // Read existing history to append
  const existingData = templateSnap.exists() ? templateSnap.data() : (await getDoc(programRef)).data() || {};
  const prevHistory = Array.isArray(existingData.publishHistory) ? existingData.publishHistory : [];
  const historyEntry = { version: templateVersion, publishedAt: now };
  const publishHistory = [historyEntry, ...prevHistory].slice(0, 50);

  const payload = { ...data, templateVersion, published: true, updatedAt: now, publishHistory };
  const clean = stripUndefined(payload);
  await setDoc(ref, clean, { merge: true });

  // Ensure programs/{catalogProgramId} has templateVersion for checkProgramUpdateAvailable
  if (catalogId !== id) {
    const catalogRef = doc(db, 'programs', catalogId);
    await setDoc(catalogRef, { templateVersion, updatedAt: now }, { merge: true });
  }

  return { templateVersion, publishHistory };
}

/**
 * @returns {Promise<Array<object>>} equipment docs (id, name, ...)
 */
export async function getEquipment() {
  const snap = await getDocs(collection(db, 'equipment'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update an equipment doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — equipment doc id
 * @param {object} data — fields to save (name, category, manufacturer, description, tags, weights_lb, photo_url, etc.)
 */
export async function updateEquipment(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'equipment', id), data, { merge: true });
}

/**
 * Delete an equipment doc from Firestore. Used by Admin Content when editing.
 * @param {string} id — equipment doc id
 */
export async function deleteEquipment(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'equipment', id));
}

/**
 * Remove a deleted equipment ID from all equipment_tiers (facility_areas and overrides).
 * @param {string} equipmentId — equipment doc id that was deleted
 */
export async function cascadeRemoveEquipmentFromTiers(equipmentId) {
  if (!equipmentId) return;
  const id = String(equipmentId);

  const tiersSnap = await getDocs(collection(db, 'equipment_tiers'));
  for (const d of tiersSnap.docs) {
    const data = d.data();
    let changed = false;
    let updates = {};

    const areas = data.facility_areas;
    if (Array.isArray(areas)) {
      const updatedAreas = areas.map((area) => {
        const eq = area.equipment;
        if (Array.isArray(eq) && eq.includes(id)) {
          changed = true;
          return { ...area, equipment: eq.filter((x) => x !== id) };
        }
        return area;
      });
      if (changed) updates.facility_areas = updatedAreas;
    }

    const overrides = data.overrides;
    if (overrides && typeof overrides === 'object' && overrides[id]) {
      const { [id]: _, ...rest } = overrides;
      updates.overrides = rest;
      changed = true;
    }

    if (changed && Object.keys(updates).length > 0) {
      await setDoc(doc(db, 'equipment_tiers', d.id), updates, { merge: true });
    }
  }
}

// ---- Equipment Tiers ----

/**
 * @returns {Promise<Array<object>>} equipment tier docs (id, name, facility_areas, overrides, ...)
 */
export async function getEquipmentTiers() {
  const snap = await getDocs(collection(db, 'equipment_tiers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update an equipment tier doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — tier doc id (begin_gym, garage_gym, home_gym)
 * @param {object} data — full tier object
 */
export async function updateEquipmentTier(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'equipment_tiers', id), { ...data, updatedAt: new Date().toISOString() }, { merge: true });
}

/**
 * Migrate all equipment tiers from JSON to Firestore. Used by "Migrate" button in Admin.
 * @param {Array<object>} tiers — array of tier objects from equipment_tiers.json
 * @returns {Promise<{ count: number }>}
 */
export async function migrateEquipmentTiers(tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return { count: 0 };
  let count = 0;
  for (const tier of tiers) {
    if (!tier.id) continue;
    await setDoc(doc(db, 'equipment_tiers', tier.id), {
      ...tier,
      updatedAt: new Date().toISOString(),
    });
    count++;
  }
  return { count };
}

/**
 * @returns {Promise<Array<object>>} exercise docs (id, name, ...)
 */
export async function getExercises() {
  const snap = await getDocs(collection(db, 'exercises'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update an exercise doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — exercise doc id
 * @param {object} data — fields to save (media, name, etc.)
 */
export async function updateExercise(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'exercises', id), data, { merge: true });
}

/**
 * Update only media.video for an exercise (preserves media.image). Used when uploading video.
 * @param {string} id — exercise doc id
 * @param {string} videoUrl — URL from Storage
 */
export async function updateExerciseVideoUrl(id, videoUrl) {
  if (!id) return;
  const docRef = doc(db, 'exercises', id);
  const snap = await getDoc(docRef);
  const current = snap.exists() ? snap.data() : {};
  const media = { ...(current.media || {}), video: videoUrl };
  await setDoc(docRef, { media }, { merge: true });
}

/**
 * Delete an exercise doc from Firestore. Used by Admin Content when editing.
 * @param {string} id — exercise doc id
 */
export async function deleteExercise(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'exercises', id));
}

/**
 * Remove a deleted exercise from all references: program palettes (exercise_ids),
 * warmup_templates exercises[], finisher_templates exercises[].
 * Microcycles are generated from mesocycle + progression, so no stored microcycle
 * docs need editing — only these sources.
 * @param {string} exerciseId — exercise doc id that was deleted
 */
export async function cascadeRemoveExerciseFromReferences(exerciseId) {
  if (!exerciseId) return;
  const id = String(exerciseId);

  const programsSnap = await getDocs(collection(db, 'programs'));
  for (const d of programsSnap.docs) {
    const data = d.data();
    const ids = data.exercise_ids;
    if (Array.isArray(ids) && ids.includes(id)) {
      await setDoc(doc(db, 'programs', d.id), { exercise_ids: ids.filter((x) => x !== id) }, { merge: true });
    }
  }

  const warmupSnap = await getDocs(collection(db, 'warmup_templates'));
  for (const d of warmupSnap.docs) {
    const data = d.data();
    const exercises = data.exercises;
    if (Array.isArray(exercises) && exercises.some((ex) => (ex.exercise_id || ex.exerciseId) === id)) {
      const filtered = exercises.filter((ex) => (ex.exercise_id || ex.exerciseId) !== id);
      await setDoc(doc(db, 'warmup_templates', d.id), { exercises: filtered }, { merge: true });
    }
  }

  const finisherSnap = await getDocs(collection(db, 'finisher_templates'));
  for (const d of finisherSnap.docs) {
    const data = d.data();
    const exercises = data.exercises;
    if (Array.isArray(exercises) && exercises.some((ex) => (ex.exercise_id || ex.exerciseId) === id)) {
      const filtered = exercises.filter((ex) => (ex.exercise_id || ex.exerciseId) !== id);
      await setDoc(doc(db, 'finisher_templates', d.id), { exercises: filtered }, { merge: true });
    }
  }

  const drillSnap = await getDocs(collection(db, 'drill_templates'));
  for (const d of drillSnap.docs) {
    const data = d.data();
    const exercises = data.exercises;
    if (Array.isArray(exercises) && exercises.some((ex) => (ex.exercise_id || ex.exerciseId) === id)) {
      const filtered = exercises.filter((ex) => (ex.exercise_id || ex.exerciseId) !== id);
      await setDoc(doc(db, 'drill_templates', d.id), { exercises: filtered }, { merge: true });
    }
  }
}

/**
 * @returns {Promise<Array<object>>} warmup template docs (id, name, duration_minutes, exercises, ...)
 */
export async function getWarmupTemplates() {
  const snap = await getDocs(collection(db, 'warmup_templates'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update a warmup template doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — warmup doc id
 * @param {object} data — name, duration_minutes, exercises[], etc.
 */
export async function updateWarmupTemplate(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'warmup_templates', id), data, { merge: true });
}

/**
 * Delete a warmup template from Firestore.
 * @param {string} id — warmup doc id
 */
export async function deleteWarmupTemplate(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'warmup_templates', id));
}

/**
 * @returns {Promise<Array<object>>} finisher template docs (id, name, duration_minutes, equipment_required, exercises, ...)
 */
export async function getFinisherTemplates() {
  const snap = await getDocs(collection(db, 'finisher_templates'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update a finisher template doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — finisher doc id
 * @param {object} data — name, duration_minutes, equipment_required[], exercises[], etc.
 */
export async function updateFinisherTemplate(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'finisher_templates', id), data, { merge: true });
}

/**
 * Delete a finisher template from Firestore.
 * @param {string} id — finisher doc id
 */
export async function deleteFinisherTemplate(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'finisher_templates', id));
}

/**
 * @returns {Promise<Array<object>>} drill template docs (id, name, duration_minutes, exercises, ...)
 */
export async function getDrillTemplates() {
  const snap = await getDocs(collection(db, 'drill_templates'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update a drill template doc in Firestore (merge). Used by Admin Content save.
 * @param {string} id — drill doc id
 * @param {object} data — name, duration_minutes, exercises[], etc.
 */
export async function updateDrillTemplate(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'drill_templates', id), data, { merge: true });
}

/**
 * @returns {Promise<Array<object>>} workout template docs (id, name, blocks: [{ block_type, template_id? }, { block_type, circuits? }], ...)
 */
export async function getWorkoutTemplates() {
  const snap = await getDocs(collection(db, 'workoutTemplates'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a single workout template by id. Tries Firestore first.
 * @param {string} id — workout template id
 * @returns {Promise<object|null>}
 */
export async function getWorkoutTemplate(id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, 'workoutTemplates', id));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
}

/**
 * Create or update a workout template in Firestore (merge).
 * Used by Admin Content Workouts. Shape: id, name, type, estimated_duration_minutes, blocks: [ warmup block, main block, finisher block ].
 * @param {string} id — workout template doc id
 * @param {object} data — full workout template (id, name, blocks, etc.)
 */
export async function updateWorkoutTemplate(id, data) {
  if (!id) return;
  await setDoc(doc(db, 'workoutTemplates', id), { ...data, updatedAt: new Date().toISOString() }, { merge: true });
}

/**
 * Delete a workout template from Firestore.
 * @param {string} id — workout template doc id
 */
export async function deleteWorkoutTemplate(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'workoutTemplates', id));
}
